import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import { FormattedMessage } from 'react-intl';

import { InfoOutlined as InfoOutlinedIcon, PriorityHighRounded as PriorityHighRoundedIcon } from 'components/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import Big from 'big.js';
import Avatar from 'components/@extended/Avatar';
import StatusChip from 'components/@extended/StatusChip';
import AuditInfo from 'components/audit/AuditInfo';
import { buttonStyle } from 'components/buttons/buttonStyle';
import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton.tsx';
import { getLabelCardColorByNumber, LabelCard } from 'components/cards/LabelCard.tsx';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import GridItemList, { filterRowList, infoLabel, Row, RowChip, SectionCard, spansRow } from 'components/GridItemList';
import InfoTooltip from 'components/InfoTooltip';
import Loader from 'components/Loader';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import AddressModal from 'components/modals/address/AddressModal';
import { useModal } from 'components/modals/useModal';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { PersonalCodeValue } from 'components/PersonalCodeShow';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Tooltip as UiTooltip } from 'components/ui/tooltip';
import MoneyDisplay from 'components/util/MoneyDisplay.tsx';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { AssetTotals } from 'pages/client-assets/AssetTotals';
import { usePartyAssetPermissions } from 'pages/client-assets/usePartyAssetPermissions';
import { getLinks } from 'pages/matters/util';
import React, { Fragment, useCallback, useContext, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { StatCard, StatFootnote, StatValue } from 'sections/stats/Stats';
import { useGeneratePartyNumberMutation, useLazyGetContactPersonalCodeQuery } from 'store/api/contacts';
import { useGetPartyAssetsTotalsQuery } from 'store/api/partyAssets';
import { useGetMinTeamsListWithFiltersQuery } from 'store/api/teams.ts';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { ClassifierData } from 'types/classifiers';
import { AddressDto, ContactDto, PartyType } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { HistoryEntityType, HistoryFieldType } from 'types/histories.ts';
import { OfficeMin } from 'types/offices';
import { TeamType } from 'types/teams.ts';
import { cn } from 'utils/cn';
import { getAddress, getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { getInitials } from 'utils/nameOrder';
import { isPartyNumberShownFor } from 'utils/partyNameUtil';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { isPersonalCodeMask } from 'utils/personalCodeMask';
import { sortByField } from 'utils/sort.ts';
import { useLastEffortType } from 'hooks/useLastEffortType';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import CustomFieldView from '../../matters/views/CustomFieldView.tsx';
import { classifierChips, ContactSectionId } from '../contactFormMapping';
import { getAdministrators, getBusinessContacts, getManagers, getPartners } from '../contactRelationUtils';
import { ContactSection } from '../form-sections/ContactSection';
import { GroupInfo } from '../GroupInfo';
import { AddressEditPencil, calculateTimeSince, employeeChips, getRelations, linkChips } from '../util';
import { Setting } from 'types/back-office-settings';
import { useSettings } from 'utils/useSettings';
import { useAdministratorField } from 'hooks/useAdministratorField';

type AddressType =
    | 'visitingAddress'
    | 'registrationAddress'
    | 'homeAddress'
    | 'registrationLocalAddress'
    | 'homeLocalAddress'
    | 'correspondenceAddress';

// `entity-link` is a targeting hook: a detail row colours it as a link at rest (index.css).
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';
// Over InfoTooltip's `h-5 w-auto`: a 1rem-tall glyph in its own 1em-wide box.
const INFO_ICON_CLASS = 'text-grey-600 h-[1rem] w-[1em]';

interface Props {
    contactId?: string;
    contact: ContactDto;
    classifiers: Record<string, ClassifierData>;
    updateAddress: (addressField: string, address: AddressDto) => Promise<void>;
    officeList: OfficeMin[] | undefined;
    onNavigateToEmployeesTab?: () => void;
    /** Takes the figures' place: a draft has none, and shows whom it is shared with there. */
    aside?: React.ReactNode;
}

/** A field's history; a draft has none to show. */
const FieldHistoryButton = ({
    contact,
    ...props
}: { contact: ContactDto } & React.ComponentProps<typeof DisplayHistoryButton>) =>
    contact.isDraft ? null : <DisplayHistoryButton {...props} />;

const MailingPreferencesDisplay: React.FC<{
    contact: ContactDto;
    classifiers: Record<string, ClassifierData>;
}> = React.memo(({ contact, classifiers }) => {
    if (!contact || !contact.mailingPreferences) return <div>-</div>;
    const items: React.ReactNode[] = [];
    contact.mailingPreferences.forEach((item, index) => {
        const optionalComma = index < (contact.mailingPreferences?.length ?? 0) - 1 ? ', ' : null;
        items.push(
            <React.Fragment key={item.mailingPreferenceTypeCode}>
                {getClassifierText(classifiers['MAILING_PREFERENCE_TYPE'], item.mailingPreferenceTypeCode)}
                {optionalComma}
            </React.Fragment>
        );
    });
    return <div>{items.length ? items : '-'}</div>;
});
MailingPreferencesDisplay.displayName = 'MailingPreferencesDisplay';

const EMPLOYEE_AVATAR_COLORS = ['#A6DD8F', '#FFC069', '#9999FF'];

const GeneralView: React.FC<Props> = ({
    classifiers,
    contact,
    updateAddress,
    officeList,
    onNavigateToEmployeesTab,
    aside,
}) => {
    const { openModal, closeModal } = useModal();
    const missingFields = useContext(DraftActionsContext)?.draft?.missingFields;
    const [generatePartyNumber] = useGeneratePartyNumberMutation();
    const { data: tenant } = useGetCurrentTenantQuery();
    const { canRead: canReadAssets } = usePartyAssetPermissions();
    const { data: assetTotals } = useGetPartyAssetsTotalsQuery(
        canReadAssets ? { filters: `party.id==${contact.id}` } : skipToken
    );
    const { revenueStatistics, relatedMatterCount } = contact;
    const dateFormat = useEffectiveDateFormat();
    const { text: timeSinceLastEffort, shouldHighlight } = calculateTimeSince(
        contact.lastBdEffort,
        contact.relationshipTypeCode,
        dateFormat
    );
    const revenuePercentage: number = revenueStatistics?.revenueYtdM1
        ? Math.round(
              (((revenueStatistics?.revenueYtd ?? 0) - revenueStatistics.revenueYtdM1) /
                  revenueStatistics.revenueYtdM1) *
                  100
          )
        : 0;
    const averageRatePercentage: number = revenueStatistics?.averageRateFyM1
        ? Math.round(
              (((revenueStatistics.averageRateFy ?? 0) - revenueStatistics.averageRateFyM1) /
                  revenueStatistics.averageRateFyM1) *
                  100
          )
        : 0;

    const hasUpdatePartyNumberPerm = useHasPermissions(['UPDATE.PARTIES.PARTY_NUMBER']);
    const hasUpdateContactTypePerm = useHasPermissions(['UPDATE.PARTIES.CONTACT_TYPE']);
    const canShowPersonalCode = useHasPermissions(['READ.PARTIES.UNHIDE_PERSONS_ID_CODE']);
    const [getPersonalCode] = useLazyGetContactPersonalCodeQuery();
    const { isSettingEnabled } = useSettings();
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const isPartyNumberGenerationEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_GENERATION);

    const isLocalizedFieldsEnabled = useIsFeatureEnabled([
        FeatureFlag.LOCALIZED_FIELDS,
        FeatureFlag.LOCALIZED_FIELDS_CONTACTS,
    ]);
    const lastEffortType = useLastEffortType();
    const lastNameFirst = useIsFeatureEnabled(FeatureFlag.LAST_NAME_FIRST);
    const isCorrespondenceAddressEnabled = isSettingEnabled(Setting.CORRESPONDENCE_ADDRESS);
    const isContactDocumentFieldsEnabled = isSettingEnabled(Setting.IDENTITY_DOCUMENT_FIELDS);
    const { isOn: isAdministratorFieldEnabled, clientLabel: clientAssistantLabel } = useAdministratorField();
    // The form shows the type control on an existing contact only with the setting, and lets it change only with the permission.
    const isContactTypeEditable = isSettingEnabled(Setting.CONTACT_TYPE_CONVERSION) && hasUpdateContactTypePerm;
    const isNew = useIsNewDesign();

    const { data: teamListData } = useGetMinTeamsListWithFiltersQuery({
        page: 0,
        pageSize: 2000,
        filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
    });
    const practiceGroupList = useMemo(() => teamListData?.content || [], [teamListData]);

    const employees = useMemo(() => {
        if (contact.partyType !== 'COMPANY' || !contact.relations) return [];
        return sortByField(
            contact.relations.filter((r) => r.partyRelationshipTypeCode === 'EMPLOYEE'),
            'otherParty.name'
        );
    }, [contact.partyType, contact.relations]);
    const { isLoading: isLoadingClassifiers, allClassifiers } = useClassifiers({
        codes: [
            'LANGUAGE',
            'CURRENCY',
            'JOB_ROLE_GROUP_TYPE',
            'COUNTRY',
            'MAILING_PREFERENCE_TYPE',
            'MARKETING_AREA_TYPE',
            'COOPERATION_AREA_TYPE',
        ],
    });

    const { initialCustomFieldsObject: initialHomeAddressCustomFieldsObject } = useCustomFields({
        entityType: EntityType.PARTY_ADDRESS,
        fields: (contact?.homeAddress?.customFields as CustomField[]) ?? [],
    });

    const { initialCustomFieldsObject: initialRegistrationAddressCustomFieldsObject } = useCustomFields({
        entityType: EntityType.PARTY_ADDRESS,
        fields: (contact?.registrationAddress?.customFields as CustomField[]) ?? [],
    });

    const { initialCustomFieldsObject: initialRegistrationLocalAddressCustomFieldsObject } = useCustomFields({
        entityType: EntityType.PARTY_ADDRESS,
        fields: (contact?.registrationLocalAddress?.customFields as CustomField[]) ?? [],
    });

    const { initialCustomFieldsObject: initialVisitingAddressCustomFieldsObject } = useCustomFields({
        entityType: EntityType.PARTY_ADDRESS,
        fields: (contact?.visitingAddress?.customFields as CustomField[]) ?? [],
    });

    const { customFields: personCustomFields } = useCustomFields({
        entityType: EntityType.PARTY_PERSON,
    });

    const { customFields: companyCustomFields } = useCustomFields({
        entityType: EntityType.PARTY_COMPANY,
    });

    const openAddressModal = useCallback(
        (key: AddressType): void => {
            const customFields = (contact?.[key]?.customFields as CustomField[]) ?? [];
            const getDeserializedCustomFields = (addressType: AddressType): Record<string, unknown> | [] => {
                switch (addressType) {
                    case 'homeAddress':
                        return initialHomeAddressCustomFieldsObject ?? [];
                    case 'registrationAddress':
                        return initialRegistrationAddressCustomFieldsObject ?? [];
                    case 'visitingAddress':
                        return initialVisitingAddressCustomFieldsObject ?? [];
                    case 'registrationLocalAddress':
                        return initialRegistrationLocalAddressCustomFieldsObject ?? [];
                    default:
                        return [];
                }
            };

            const deserializedCustomFields = getDeserializedCustomFields(key);

            openModal(
                <AddressModal
                    onChange={(value) => {
                        void updateAddress(key, value);
                    }}
                    initialData={{
                        additionalInformation: (contact?.[key] as AddressDto)?.additionalInformation ?? '',
                        address1: (contact?.[key] as AddressDto)?.address1 ?? '',
                        address2: (contact?.[key] as AddressDto)?.address2 ?? '',
                        cityName: (contact?.[key] as AddressDto)?.cityName ?? '',
                        countryCode:
                            (
                                getValidOptionsFromClassifier(classifiers['COUNTRY']) as {
                                    label: string;
                                    value: string;
                                }[]
                            ).find((val) => val.value === (contact?.[key] as AddressDto)?.countryCode) ?? null,
                        zipCode: (contact?.[key] as AddressDto)?.zipCode ?? '',
                        ...deserializedCustomFields,
                    }}
                    onClose={closeModal}
                    classifiers={classifiers}
                    customFields={customFields}
                    isBillingAddress={false}
                    sourceAddressForCopy={undefined}
                />,
                closeModal
            );
        },
        [
            contact,
            openModal,
            classifiers,
            closeModal,
            initialHomeAddressCustomFieldsObject,
            initialRegistrationAddressCustomFieldsObject,
            initialVisitingAddressCustomFieldsObject,
            initialRegistrationLocalAddressCustomFieldsObject,
            updateAddress,
        ]
    );

    const getAddressText = (key: AddressType) => {
        if (!contact) return '-';
        const address = contact?.[key] ?? null;
        const countryLabel =
            classifiers?.['COUNTRY'] && address
                ? getClassifierText(classifiers?.['COUNTRY'], address?.countryCode)
                : undefined;
        return getAddress(address as AddressDto, countryLabel);
    };

    const getEditAddressButton = (key: AddressType) => {
        if (!contact) return <div />;
        // A draft is edited in its form only.
        if (contact.isDraft) return undefined;
        if (isNew) {
            return <AddressEditPencil onClick={() => openAddressModal(key)} />;
        }
        return (
            <Button
                {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'bg-transparent')}
                onClick={() => {
                    openAddressModal(key);
                }}
            >
                <FormattedMessage id="edit" />
            </Button>
        );
    };

    const getGeneratePartyNumberButton = (id: number) => {
        // A draft is numbered when it is created.
        if (!hasUpdatePartyNumberPerm || contact.isDraft) {
            return undefined;
        }

        if (!contact) return <div />;
        return (
            <Button
                {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'bg-transparent')}
                onClick={() => {
                    generatePartyNumber({ id: String(id) });
                }}
            >
                <FormattedMessage id="generate" />
            </Button>
        );
    };

    const getPartyToPartyRelations = useCallback(
        (key: 'relations' | 'relatesTo', type: 'EMPLOYEE' | 'PRIMARY_CONTACT') => {
            if (!contact) return <div />;
            const links: React.ReactNode[] = [];
            const relationsCopy = contact?.[key] || [];
            const employees = sortByField(relationsCopy, 'otherParty.name').filter(
                (item) => item.partyRelationshipTypeCode === type
            );
            employees.forEach((item) => {
                links.push(
                    <div key={item.otherParty.id}>
                        <PartyName
                            party={item.otherParty}
                            isLink={!item.otherParty.deletedAt}
                            variant="readonly-self"
                        />
                    </div>
                );
            });
            return <div className="flex flex-col flex-wrap">{links.length ? links : '-'}</div>;
        },
        [contact]
    );

    const getBusinessAreas = useCallback(() => {
        if (!contact) return <div />;
        const items: React.ReactNode[] = [];
        (contact?.businessAreas || []).forEach((item, index) => {
            const optionalComma = index < contact?.businessAreas.length - 1 ? `,\u00A0` : null;
            items.push(
                <React.Fragment key={item.businessAreaCode}>
                    {getClassifierText(classifiers['BUSINESS_AREA_TYPE'], item.businessAreaCode)}
                    {optionalComma}
                </React.Fragment>
            );
        });
        return <div>{items.length ? items : '-'}</div>;
    }, [contact, classifiers]);

    const renderWebsiteLink = (website: string | null | undefined) => {
        if (!website || website === '-') return '-';

        const websitePattern =
            /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g;
        if (!websitePattern.test(website)) return website;

        const url = website.startsWith('http') ? website : `https://${website}`;
        const maxLength = 40;
        const displayText = website.length > maxLength ? `${website.substring(0, maxLength)}…` : website;

        return (
            <a className={LINK_CLASS} href={url} target="_blank" rel="noopener noreferrer">
                {displayText}
            </a>
        );
    };

    // Marketing area type list extracted into <MarketingAreaTypesList /> below to short-circuit rendering on early-return.

    const getCountries = useMemo(() => {
        if (!contact || !contact.countries) return <div>-</div>;
        const items: React.ReactNode[] = [];
        contact.countries.forEach((item, index) => {
            const optionalComma = index < (contact.countries?.length ?? 0) - 1 ? ', ' : null;
            items.push(
                <React.Fragment key={item.countryTypeCode}>
                    {getClassifierText(classifiers['COUNTRY'], item.countryTypeCode)}
                    {optionalComma}
                </React.Fragment>
            );
        });
        return <div>{items.length ? items : '-'}</div>;
    }, [contact, classifiers]);

    const officeListSorted = useMemo(() => {
        return (officeList || [])
            .filter((office) => office.isActive !== false)
            .toSorted((office) =>
                contact?.servedByOffices?.some((servedOffice) => servedOffice.id === office.id) ? -1 : 0
            );
    }, [officeList, contact]);

    const practiceGroupListSorted = useMemo(() => {
        return (practiceGroupList ?? []).toSorted((pg) =>
            contact?.servedByPracticeGroups?.some((servedPg) => servedPg.id === pg.id) ? -1 : 0
        );
    }, [practiceGroupList, contact]);

    if (!contact || isLoadingClassifiers) {
        return <Loader />;
    }

    const isRelationshipTypeNonClientOrLead =
        contact?.relationshipTypeCode === 'NON-CLIENT' || contact?.relationshipTypeCode === 'LEAD';

    const servedByOfficesExplanation = t('contacts.tooltips.served-by-offices-detail');
    const servedByPracticeGroupsExplanation = t('contacts.tooltips.served-by-practice-groups-detail');

    // Rows named for the form section that edits them: classic groups them its own way, the new design by section.

    const partyNumberRow = {
        content: contact?.partyNumber ?? '-',
        label: (
            <FormattedMessage
                id={
                    contact?.relationshipTypeCode === 'CLIENT'
                        ? 'contacts.client-number-label'
                        : 'contacts.party-number-label'
                }
            />
        ),
        button:
            !contact?.partyNumber && isPartyNumberGenerationEnabled
                ? getGeneratePartyNumberButton(contact.id)
                : undefined,
        hidden: !isPartyNumberShownFor(tenant, contact.relationshipTypeCode) || !isPartyNumberUsageEnabled,
    };

    const titleRow = {
        content: contact?.displayedPersonTitle ?? '-',
        label: <FormattedMessage id="title" />,
    };

    const nameRows = (() => {
        const firstNameItem = {
            content: isNew ? (
                (contact.firstName ?? '-')
            ) : (
                <span className="text-body1">{contact?.firstName ?? '-'}</span>
            ),
            label: (
                <div className="flex gap-[.5ch]">
                    <FormattedMessage id="first-name" />
                    <FieldHistoryButton
                        contact={contact}
                        entityType={HistoryEntityType.PARTY}
                        entityId={`${contact.id}`}
                        fieldType={HistoryFieldType.PERSON_OFFICIAL_NAME}
                        fieldName={t('first-name')}
                        activity={t('updating')}
                    />
                </div>
            ),
        };
        const lastNameItem = {
            content: isNew ? (contact.lastName ?? '-') : <span className="text-body1">{contact?.lastName ?? '-'}</span>,
            label: (
                <div className="flex gap-[.5ch]">
                    <FormattedMessage id="last-name" />
                    <FieldHistoryButton
                        contact={contact}
                        entityType={HistoryEntityType.PARTY}
                        entityId={`${contact.id}`}
                        fieldType={HistoryFieldType.PERSON_OFFICIAL_NAME}
                        fieldName={t('last-name')}
                        activity={t('updating')}
                    />
                </div>
            ),
        };
        return lastNameFirst ? [lastNameItem, firstNameItem] : [firstNameItem, lastNameItem];
    })();

    const localNameRows = isLocalizedFieldsEnabled
        ? [
              {
                  content:
                      contact?.localFirstName || contact?.localLastName
                          ? `${contact?.localFirstName ?? ''} ${contact?.localLastName ?? ''}`
                          : '-',
                  label: <FormattedMessage id="contacts.person.name-local" />,
              },
          ]
        : [];

    const personalCodeRow = {
        content: isPersonalCodeMask(contact.personalCode) ? (
            <PersonalCodeValue
                key={contact.id}
                code={contact.personalCode}
                canShow={canShowPersonalCode}
                request={() => getPersonalCode(String(contact.id))}
            />
        ) : (
            (contact.personalCode ?? '-')
        ),
        label: <FormattedMessage id="contacts.person-id" />,
    };

    const positionRow = { content: contact?.position ?? '-', label: <FormattedMessage id="contacts.position" /> };

    // Alphabetical, as in the field's picker: a firm adds its own values, so the classifier's order is the order added.
    const areaOfResponsibilityChips = classifierChips(
        classifiers['AREA_OF_RESPONSIBILITY_TYPE'],
        contact.areaOfResponsibilityTypes?.map((area) => area.areaOfResponsibilityTypeCode)
    ).toSorted((a, b) => String(a.label).localeCompare(String(b.label)));

    const areaOfResponsibilityRow = {
        content: areaOfResponsibilityChips.map((chip) => chip.label).join(', ') || '-',
        label: <FormattedMessage id="contacts.areas-of-responsibility" />,
    };

    // A person's keywords are edited beside the areas of responsibility, so they are read there too.
    const keywordChips = classifierChips(
        classifiers['KEYWORD_TYPE'],
        contact.relatedKeywords?.map((item) => item.keywordTypeCode)
    );

    const keywordRow = {
        content: keywordChips.map((chip) => chip.label).join(', ') || '-',
        label: <FormattedMessage id="contacts.related-keywords" />,
    };

    const relationshipGrowthKeyword = contact.relatedKeywords?.find(
        (item) => item.keywordTypeCode === 'RELATIONSHIP_GROWTH'
    );

    const relationshipGrowthRow = {
        label: <FormattedMessage id="contacts.reason-for-relationship-growth-potential" />,
        content: relationshipGrowthKeyword?.notes ?? '-',
        multiline: true,
        hidden: !relationshipGrowthKeyword,
    };

    const linkedinRow = { content: contact?.linkedinUrl ?? '-', label: <FormattedMessage id="contacts.linkedin" /> };

    const homeLocalAddressRows = isLocalizedFieldsEnabled
        ? [
              {
                  button: getEditAddressButton('homeLocalAddress'),
                  label: <FormattedMessage id="address-local" />,
                  content: contact.homeLocalAddress ? getAddressText('homeLocalAddress') : '-',
              },
          ]
        : [];

    const personBusinessAreasRow = {
        content: getBusinessAreas(),
        label: <FormattedMessage id="business-area" />,
        formLabels: [t('business-areas')],
        hidden: contact.relationshipTypeCode !== 'CLIENT',
    };

    const businessContactsRow = {
        content: contact && contact.businessContacts ? getRelations(contact, ['BUSINESS_CONTACT_OF']) : '-',
        label: <FormattedMessage id="contacts.business-contact-of" />,
        hidden: contact.partyType !== 'PERSON',
    };

    const segmentText = contact.segmentTypeCode
        ? getClassifierText(classifiers['SEGMENT_TYPE'], contact.segmentTypeCode)
        : '-';
    const segmentRow = {
        label: (
            <div className="flex gap-[.5ch]">
                <FormattedMessage id="contacts.segment-type" />
                <FieldHistoryButton
                    contact={contact}
                    entityType={HistoryEntityType.PARTY}
                    entityId={`${contact.id}`}
                    fieldType={HistoryFieldType.SEGMENT_TYPE_CODE}
                    fieldName={t('contacts.segment-type')}
                    activity={t('updating')}
                />
            </div>
        ),
        content: isNew ? segmentText : <span className="text-body1">{segmentText}</span>,
    };

    const serviceLevelText = contact.serviceLevelTypeCode
        ? getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], contact.serviceLevelTypeCode)
        : '-';
    const serviceLevelRow = {
        label: (
            <div className="flex gap-[.5ch]">
                <FormattedMessage id="contacts.service-level-type" />
                <FieldHistoryButton
                    contact={contact}
                    entityType={HistoryEntityType.PARTY}
                    entityId={`${contact.id}`}
                    fieldType={HistoryFieldType.SERVICE_LEVEL_TYPE_CODE}
                    fieldName={t('contacts.service-level-type')}
                    activity={t('updating')}
                />
            </div>
        ),
        content: isNew ? serviceLevelText : <span className="text-body1">{serviceLevelText}</span>,
    };

    const personServedByOfficesRow = {
        label: (
            <span className="flex items-center gap-1">
                <FormattedMessage id="contacts.served-by-offices" />
                <InfoTooltip text="contacts.tooltips.served-by-offices-detail" className={INFO_ICON_CLASS} />
            </span>
        ),
        content: (
            <span className="flex flex-wrap gap-1">
                {officeListSorted?.map((office) => (
                    <StatusChip
                        key={office.id}
                        label={office.abbreviation}
                        type={
                            contact?.servedByOffices?.some((servedOffice) => servedOffice.id === office.id)
                                ? 'neutral'
                                : 'inactive'
                        }
                    />
                ))}
            </span>
        ),
        hidden: isRelationshipTypeNonClientOrLead,
    };

    const personServedByPracticeGroupsRow = {
        label: (
            <span className="flex items-center gap-1">
                <FormattedMessage id="contacts.served-by-practice-groups" />
                <InfoTooltip text="contacts.tooltips.served-by-practice-groups-detail" className={INFO_ICON_CLASS} />
            </span>
        ),
        content: (
            <span className="flex flex-wrap gap-1">
                {practiceGroupListSorted?.length
                    ? practiceGroupListSorted?.map((pg) => (
                          <StatusChip
                              key={pg.id}
                              label={pg.abbreviation}
                              type={
                                  contact?.servedByPracticeGroups?.some(
                                      (servedPracticeGroup) => servedPracticeGroup.id === pg.id
                                  )
                                      ? 'neutral'
                                      : 'inactive'
                              }
                          />
                      ))
                    : '-'}
            </span>
        ),
        hidden: isRelationshipTypeNonClientOrLead,
    };

    const identityRows = [
        {
            content: contact?.identityDocumentNumber ?? '-',
            label: <FormattedMessage id="contacts.identity-document.number" />,
            hidden: !isContactDocumentFieldsEnabled,
        },
        {
            content: isNew ? (
                <FormattedDate value={contact.identityDocumentDateOfIssue} />
            ) : (
                (contact?.identityDocumentDateOfIssue ?? '-')
            ),
            label: <FormattedMessage id="contacts.identity-document.date-of-issue" />,
            hidden: !isContactDocumentFieldsEnabled,
        },
        {
            content: isNew ? (
                <FormattedDate value={contact.identityDocumentDateOfExpiry} />
            ) : (
                (contact?.identityDocumentDateOfExpiry ?? '-')
            ),
            label: <FormattedMessage id="contacts.identity-document.date-of-expiry" />,
            hidden: !isContactDocumentFieldsEnabled,
        },
        {
            content: contact?.identityDocumentIssuingCountryCode
                ? getClassifierText(classifiers['COUNTRY'], contact.identityDocumentIssuingCountryCode)
                : '-',
            label: <FormattedMessage id="contacts.identity-document.issuing-country" />,
            hidden: !isContactDocumentFieldsEnabled,
        },
        {
            content: contact?.identityDocumentIssuingAuthority ?? '-',
            label: <FormattedMessage id="contacts.identity-document.issuing-authority" />,
            hidden: !isContactDocumentFieldsEnabled,
        },
    ];

    const officialNameRow = {
        content: isNew ? (
            (contact.officialName ?? '-')
        ) : (
            <span className="text-body1">{contact?.officialName ?? '-'}</span>
        ),
        label: (
            <div className="flex gap-[.5ch]">
                <FormattedMessage id="contacts.company.name" />
                <FieldHistoryButton
                    contact={contact}
                    entityType={HistoryEntityType.PARTY}
                    entityId={`${contact.id}`}
                    fieldType={HistoryFieldType.COMPANY_OFFICIAL_NAME}
                    fieldName={t('contacts.company.name')}
                    activity={t('updating')}
                />
            </div>
        ),
    };

    const officialLocalNameRows = isLocalizedFieldsEnabled
        ? [
              {
                  content: contact?.officialLocalName ?? '-',
                  label: <FormattedMessage id="contacts.company.name-local" />,
              },
          ]
        : [];

    const regCodeRow = { content: contact?.regCode ?? '-', label: <FormattedMessage id="reg-number" /> };

    const registrationAddressRow = {
        button: getEditAddressButton('registrationAddress'),
        label: <FormattedMessage id="registration-address" />,
        content: contact.registrationAddress ? getAddressText('registrationAddress') : '-',
    };

    const registrationLocalAddressRows = isLocalizedFieldsEnabled
        ? [
              {
                  button: getEditAddressButton('registrationLocalAddress'),
                  label: <FormattedMessage id="registration-address-local" />,
                  content: contact.registrationLocalAddress ? getAddressText('registrationLocalAddress') : '-',
              },
          ]
        : [];

    const companyBusinessAreasRow = {
        content: getBusinessAreas(),
        label: <FormattedMessage id="business-area" />,
        formLabels: [t('business-areas')],
    };

    const companyServedByOfficesRow = {
        label: (
            <span className="inline-flex items-center gap-1">
                <FormattedMessage id="contacts.served-by-offices" />
                <UiTooltip content={servedByOfficesExplanation}>
                    <span tabIndex={0} role="img" aria-label={servedByOfficesExplanation} className="inline-flex">
                        <InfoOutlinedIcon className="text-grey-600 size-4" />
                    </span>
                </UiTooltip>
            </span>
        ),
        content: (
            <span className="flex flex-wrap gap-1">
                {officeListSorted?.map((office) => (
                    <StatusChip
                        key={office.id}
                        label={office.abbreviation}
                        type={
                            contact?.servedByOffices?.some((servedOffice) => servedOffice.id === office.id)
                                ? 'neutral'
                                : 'inactive'
                        }
                    />
                ))}
            </span>
        ),
        hidden: isRelationshipTypeNonClientOrLead,
    };

    const companyServedByPracticeGroupsRow = {
        label: (
            <span className="inline-flex items-center gap-1">
                <FormattedMessage id="contacts.served-by-practice-groups" />
                <UiTooltip content={servedByPracticeGroupsExplanation}>
                    <span
                        tabIndex={0}
                        role="img"
                        aria-label={servedByPracticeGroupsExplanation}
                        className="inline-flex"
                    >
                        <InfoOutlinedIcon className="text-grey-600 size-4" />
                    </span>
                </UiTooltip>
            </span>
        ),
        content: (
            <span className="flex flex-wrap gap-1">
                {practiceGroupListSorted?.length
                    ? practiceGroupListSorted?.map((pg) => (
                          <StatusChip
                              key={pg.id}
                              label={pg.abbreviation}
                              type={
                                  contact?.servedByPracticeGroups?.some(
                                      (servedPracticeGroup) => servedPracticeGroup.id === pg.id
                                  )
                                      ? 'neutral'
                                      : 'inactive'
                              }
                          />
                      ))
                    : '-'}
            </span>
        ),
        hidden: isRelationshipTypeNonClientOrLead,
    };

    const phoneRow = { content: contact?.mainPhone ?? '-', label: <FormattedMessage id="phone" /> };

    const emailRow = { content: contact?.mainEmail ?? '-', label: <FormattedMessage id="email" /> };

    const mobileRow = { content: contact?.mainMobile ?? '-', label: <FormattedMessage id="mobile-phone" /> };

    const websiteRow =
        contact.partyType === 'COMPANY'
            ? { content: renderWebsiteLink(contact?.website), label: <FormattedMessage id="website" /> }
            : null;

    const correspondenceAddressRow = isCorrespondenceAddressEnabled
        ? {
              label: <FormattedMessage id="contacts.correspondence-address" />,
              button: getEditAddressButton('correspondenceAddress'),
              content: getAddressText('correspondenceAddress'),
          }
        : null;

    const visitingAddressRow =
        contact.partyType === 'COMPANY'
            ? {
                  label: <FormattedMessage id="visiting-address" />,
                  button: getEditAddressButton('visitingAddress'),
                  content: getAddressText('visitingAddress'),
              }
            : null;

    const homeAddressRow =
        contact.partyType === 'PERSON'
            ? {
                  content: contact?.homeAddress ? getAddressText('homeAddress') : '-',
                  label: <FormattedMessage id="address" />,
                  button: getEditAddressButton('homeAddress'),
              }
            : null;

    const primaryContactRow =
        contact.partyType === 'COMPANY'
            ? {
                  content: getPartyToPartyRelations('relations', 'PRIMARY_CONTACT'),
                  label: <FormattedMessage id="contacts.primary-contact" />,
              }
            : null;

    const primaryContactForCompaniesRow =
        contact.partyType === 'PERSON'
            ? {
                  content: getPartyToPartyRelations('relatesTo', 'PRIMARY_CONTACT'),
                  label: <FormattedMessage id="contacts.primary-contact-for-companies" />,
              }
            : null;

    const employedByRow =
        contact.partyType === 'PERSON'
            ? {
                  content: getPartyToPartyRelations('relatesTo', 'EMPLOYEE'),
                  label: <FormattedMessage id="contacts.employed-by" />,
              }
            : null;

    const uboRow =
        contact.partyType === 'PERSON'
            ? {
                  content: contact?.uboForCompanies?.length
                      ? getLinks('contact', contact.uboForCompanies, { target: '_blank' })
                      : '-',
                  label: <FormattedMessage id="contacts.ubo-for-companies" />,
              }
            : null;

    const groupsRow = {
        content: contact?.partyGroups?.length ? (
            <div className="flex flex-wrap items-center gap-1">
                {contact.partyGroups.map((group, index) => (
                    <Fragment key={group.id}>
                        <div className="flex items-center gap-1">
                            <RouterLink className={LINK_CLASS} to={`/group/${group.id}`}>
                                {group.groupName}
                            </RouterLink>
                            <GroupInfo group={group} classifiers={classifiers} />
                        </div>
                        {index < (contact.partyGroups?.length ?? 0) - 1 && <span className="text-body1">,</span>}
                    </Fragment>
                ))}
            </div>
        ) : (
            '-'
        ),
        label: <FormattedMessage id="contacts.other.belongs-to-groups" />,
    };

    const marketingRows = [
        {
            content: contact?.preferredMailingLanguageTypeCode
                ? getClassifierText(classifiers['LANGUAGE'], contact.preferredMailingLanguageTypeCode)
                : '-',
            label: <FormattedMessage id="contacts.marketing.preferred-mailing-language" />,
        },
        {
            content: <MailingPreferencesDisplay contact={contact} classifiers={classifiers} />,
            label: <FormattedMessage id="contacts.marketing.mailing-preferences" />,
        },
        {
            content: (
                <MarketingAreaTypesList marketingAreaTypes={contact.marketingAreaTypes} classifiers={classifiers} />
            ),
            label: <FormattedMessage id="contacts.marketing.preferred-marketing-areas" />,
        },
        {
            content: getCountries,
            label: <FormattedMessage id="contacts.marketing.jurisdictions-of-interest" />,
        },
    ];

    const data = {
        person:
            contact.partyType === 'PERSON'
                ? [
                      partyNumberRow,
                      titleRow,
                      ...nameRows,
                      ...localNameRows,
                      personalCodeRow,
                      positionRow,
                      keywordRow,
                      areaOfResponsibilityRow,
                      relationshipGrowthRow,
                      linkedinRow,
                      ...homeLocalAddressRows,
                      personBusinessAreasRow,
                      businessContactsRow,
                      segmentRow,
                      serviceLevelRow,
                      personServedByOfficesRow,
                      personServedByPracticeGroupsRow,
                      ...identityRows,
                  ]
                : [],
        company: [
            partyNumberRow,
            officialNameRow,
            ...officialLocalNameRows,
            regCodeRow,
            registrationAddressRow,
            ...registrationLocalAddressRows,
            companyBusinessAreasRow,
            segmentRow,
            serviceLevelRow,
            companyServedByOfficesRow,
            companyServedByPracticeGroupsRow,
        ],
        contact: [
            phoneRow,
            emailRow,
            mobileRow,
            websiteRow,
            correspondenceAddressRow,
            visitingAddressRow,
            homeAddressRow,
            primaryContactRow,
            primaryContactForCompaniesRow,
            employedByRow,
            uboRow,
            groupsRow,
        ],
        marketing: marketingRows,
    };

    const baseCurrencyValues = {
        tenant_base_currency: revenueStatistics?.baseCurrencyTypeCode ?? tenant?.baseCurrencyTypeCode,
    };
    const figures: {
        labelId: string;
        /** Shown instead of the labelId's message. */
        label?: string;
        tooltipText?: string;
        tooltipValues?: React.ComponentProps<typeof FormattedMessage>['values'];
        value: React.ReactNode;
        /** The comparison under the value. */
        footnote?: string | null;
        color?: 'info' | 'success' | 'error';
        /** Drawn in danger on the new design's card, which carries none of `color`'s tints. */
        overdue?: boolean;
        large?: boolean;
        hidden?: boolean;
    }[] = [
        {
            labelId: 'contacts.last-bd-effort',
            value: timeSinceLastEffort,
            color: shouldHighlight ? 'error' : 'info',
            overdue: shouldHighlight,
        },
        {
            labelId: 'contacts.last-effort-activity',
            label: lastEffortType?.label,
            value: calculateTimeSince(contact.lastHCSBdEffort, null, dateFormat).text,
            color: 'info',
            hidden: !lastEffortType,
        },
        {
            labelId: 'contacts.invoiced-revenue-ytd',
            tooltipText: 'contacts.tooltips.invoiced-revenue-ytd',
            large: true,
            value: (
                <MoneyDisplay
                    amount={revenueStatistics?.revenueYtd ?? 0}
                    currency={revenueStatistics?.baseCurrencyTypeCode}
                    decimalScale={0}
                />
            ),
            footnote: revenueStatistics?.revenueYtdM1
                ? `${revenuePercentage > 0 ? '+' : ''}${revenuePercentage}% vs LYTD`
                : null,
            color: getLabelCardColorByNumber(revenuePercentage),
        },
        {
            labelId: 'contacts.invoiced-revenue-3y',
            tooltipText: 'contacts.tooltips.invoiced-revenue-3y',
            large: true,
            value: (
                <MoneyDisplay
                    amount={revenueStatistics?.revenueTotalLastQuarter3y ?? 0}
                    currency={revenueStatistics?.baseCurrencyTypeCode}
                    decimalScale={0}
                />
            ),
        },
        {
            labelId: 'contacts.case-count-3y',
            tooltipText: 'contacts.tooltips.case-count-3y',
            large: true,
            value: relatedMatterCount ?? 0,
        },
        {
            labelId: 'contacts.average-rate-ytd',
            tooltipText: 'contacts.tooltips.average-rate-ytd',
            large: true,
            value: (
                <MoneyDisplay
                    amount={revenueStatistics?.averageRateFy ?? 0}
                    currency={revenueStatistics?.baseCurrencyTypeCode}
                />
            ),
            footnote: revenueStatistics?.averageRateFyM1
                ? `vs. ${revenueStatistics?.averageRateFyM1 ?? '0.00'} LY (${averageRatePercentage > 0 ? '+' : ''}${new Big(averageRatePercentage).toFixed(2)}%)`
                : null,
            color: getLabelCardColorByNumber(averageRatePercentage),
        },
        {
            labelId: 'contacts.outstanding-balance',
            tooltipText: 'contacts.tooltips.outstanding-balance',
            tooltipValues: baseCurrencyValues,
            large: true,
            value: (
                <MoneyDisplay
                    amount={revenueStatistics?.outstandingBalance ?? 0}
                    currency={revenueStatistics?.baseCurrencyTypeCode}
                />
            ),
        },
        {
            labelId: 'contacts.overdue-amount',
            tooltipText: 'contacts.tooltips.overdue-amount',
            tooltipValues: baseCurrencyValues,
            large: true,
            value: (
                <MoneyDisplay
                    amount={revenueStatistics?.overdueAmount ?? 0}
                    currency={revenueStatistics?.baseCurrencyTypeCode}
                />
            ),
        },
        {
            labelId: 'client-assets.figure',
            // Normal size: a sum per currency, each with its code, runs past the card at the large one.
            value: assetTotals ? (
                <AssetTotals totals={assetTotals} emptyCurrency={tenant?.baseCurrencyTypeCode} />
            ) : null,
            hidden: !canReadAssets,
        },
    ];
    const shownFigures = contact.isDraft ? [] : figures.filter((figure) => !figure.hidden);

    const labelCardsSequence = (
        <div className="flex flex-col gap-4">
            {shownFigures.map((figure) => (
                <LabelCard
                    key={figure.labelId}
                    label={figure.label ?? <FormattedMessage id={figure.labelId} />}
                    tooltipText={figure.tooltipText}
                    tooltipValues={figure.tooltipValues}
                    fontSize={figure.large ? 'large' : 'normal'}
                    content={
                        <>
                            {figure.value}
                            {figure.footnote ? <div className="text-body1 font-normal">{figure.footnote}</div> : null}
                        </>
                    }
                    color={figure.color}
                />
            ))}
        </div>
    );

    // The new design draws the contact's figures with the case's figure cards, on the case's card classes.
    const figureCards = (
        <div className="flex min-w-0 flex-col gap-(--card-gap)">
            {shownFigures.map((figure) => (
                <StatCard key={figure.labelId} className="rounded-[10px]" contentClassName="px-5 py-4">
                    <span className="flex items-center gap-1">
                        {figure.label ?? <FormattedMessage id={figure.labelId} />}
                        {figure.tooltipText ? (
                            <InfoTooltip
                                text={figure.tooltipText}
                                values={figure.tooltipValues}
                                className={INFO_ICON_CLASS}
                            />
                        ) : null}
                    </span>
                    <StatValue className={figure.overdue ? 'text-danger-ink' : undefined}>{figure.value}</StatValue>
                    {figure.footnote ? <StatFootnote>{figure.footnote}</StatFootnote> : null}
                </StatCard>
            ))}
        </div>
    );

    const isAddingNewCasesBlocked = contact.isAddingNewCasesBlocked ?? false;
    const isAddingTimecardsBlocked = contact.isAddingTimecardsBlocked ?? false;
    const anyTimecardsBlocked = isAddingTimecardsBlocked;
    const isBlocked = isAddingNewCasesBlocked || anyTimecardsBlocked;

    const blockingAlertMessageId =
        isAddingNewCasesBlocked && anyTimecardsBlocked
            ? 'contacts.combined-blocking-alert-message'
            : isAddingNewCasesBlocked
              ? 'contacts.blocking-alert-message'
              : anyTimecardsBlocked
                ? 'contacts.timecard-blocking-alert-message'
                : null;

    const blockingAlert = isBlocked && blockingAlertMessageId && (
        // A notice that is there as the record opens, not an error that just happened: polite, not an alert.
        <Alert
            role="status"
            className="border-error bg-error-light text-fg new:mb-(--card-gap) mb-6 flex items-center gap-2 py-2 pr-4 pl-3 font-semibold"
        >
            {/* A wrapper, not a bare svg child: the kit alert lays a direct svg out as a grid column. */}
            <span className="flex w-6 shrink-0 justify-center">
                <PriorityHighRoundedIcon className="text-error size-4" />
            </span>
            <div className="min-w-0">
                <FormattedMessage id={blockingAlertMessageId} />
            </div>
        </Alert>
    );

    const employeesCard = contact.partyType === 'COMPANY' && employees.length > 0 && (
        <section className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden overflow-hidden rounded-[10px] border">
            <div className="border-divider border-b px-4 py-3 text-base leading-6 font-semibold">
                <FormattedMessage id="employees.employees" />
            </div>
            <ul>
                {employees.slice(0, 3).map((emp, index) => (
                    <li
                        key={emp.otherParty.id}
                        className={cn(
                            'grid-item-list-row border-divider mx-4 flex items-center py-3 text-sm leading-[1.5715]',
                            index < Math.min(employees.length, 3) - 1 && 'border-b'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <RouterLink
                                to={emp.otherParty.deletedAt ? '#' : `/contact/${emp.otherParty.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0 no-underline"
                            >
                                {/* Classic's fill is data-driven, rotating per row, so it rides a custom property;
                                    the new design draws every avatar in the primary colour. */}
                                <span
                                    className="new:bg-primary flex size-10 items-center justify-center rounded-full bg-(--avatar-bg)"
                                    style={
                                        {
                                            '--avatar-bg':
                                                EMPLOYEE_AVATAR_COLORS[index % EMPLOYEE_AVATAR_COLORS.length],
                                        } as React.CSSProperties
                                    }
                                >
                                    <span className="text-body2 text-fg new:text-primary-foreground font-semibold">
                                        {getInitials(emp.otherParty.name)}
                                    </span>
                                </span>
                            </RouterLink>
                            <div>
                                <PartyName
                                    party={emp.otherParty}
                                    isLink={!emp.otherParty.deletedAt}
                                    linkTarget="_blank"
                                    variant="readonly-other"
                                    className="font-semibold"
                                />
                                {emp.otherParty.position && (
                                    <p className="text-body2 text-fg-secondary">{emp.otherParty.position}</p>
                                )}
                            </div>
                        </div>
                    </li>
                ))}
                {employees.length > 3 && onNavigateToEmployeesTab && (
                    <li>
                        <Button
                            {...buttonStyle(
                                { variant: 'outlined' },
                                'w-full rounded-none border-x-0 border-b-0 shadow-none'
                            )}
                            onClick={onNavigateToEmployeesTab}
                        >
                            <FormattedMessage id="employees.see-more" />
                        </Button>
                    </li>
                )}
            </ul>
        </section>
    );

    const customFieldView =
        (personCustomFields.length > 0 && contact.partyType === PartyType.PERSON) ||
        (companyCustomFields.length > 0 && contact.partyType === PartyType.COMPANY) ? (
            <CustomFieldView
                fields={(contact.customFields as CustomField[]) || []}
                classifiers={allClassifiers}
                singleColumn
            />
        ) : null;

    const auditInfo = (
        <AuditInfo
            createdAt={contact.createdAt}
            createdByEmployee={contact.createdByEmployee}
            lastModifiedAt={contact.lastModifiedAt}
            changedByEmployee={contact.changedByEmployee}
        />
    );

    if (isNew) {
        // Each section lists its form's fields in the form's order, each under the form's label and where the form
        // shows it, so a row and its field share a cell. The rows no field edits follow, as `readOnly` has it.
        const section = (id: ContactSectionId, titleId: string, rows: Row[]) => (
            <ContactSection id={id} titleId={titleId} contact={contact} rows={rows}>
                {(edit) => (
                    <GridItemList isNew titleId={titleId} data={withMissing(rows, missingFields)} button={edit} />
                )}
            </ContactSection>
        );
        const isPerson = contact.partyType === PartyType.PERSON;
        const isClient = contact.relationshipTypeCode === 'CLIENT';
        // A multi-select's row: its field's label over its values as the field's chips, in the field's order.
        const chipsRow = (
            labelId: string,
            chips: RowChip[],
            { hidden, hintId }: { hidden?: boolean; hintId?: string } = {}
        ): Row => ({
            label: hintId ? infoLabel(labelId, hintId) : <FormattedMessage id={labelId} />,
            content: '-',
            chips,
            hidden,
        });
        const businessAreaChips = classifierChips(
            classifiers['BUSINESS_AREA_TYPE'],
            contact.businessAreas?.map((area) => area.businessAreaCode)
        );
        // The new design names what serves the contact as plain text, not as a chip per office of the firm. No field
        // edits the list, so it wraps onto as many lines as it needs rather than being cut at one.
        const servedBy = (row: Row, served?: { name: string }[]): Row => ({
            ...row,
            content: served?.length ? served.map((item) => item.name).join(', ') : '-',
            multiline: true,
            readOnly: true,
        });
        const [servedByOfficesRow, servedByPracticeGroupsRow] = isPerson
            ? [personServedByOfficesRow, personServedByPracticeGroupsRow]
            : [companyServedByOfficesRow, companyServedByPracticeGroupsRow];
        const aboutRows: Row[] = [
            {
                label: <FormattedMessage id="contacts.about.relationship-type" />,
                content: getClassifierText(classifiers['RELATIONSHIP_TYPE'], contact.relationshipTypeCode) || '-',
                // Its radio options take a row of their own, whatever is picked; so do the contact type's.
                wide: 'always',
            },
            chipsRow(
                'contacts.about.relationship-partner',
                employeeChips(getPartners(contact.partners ?? null), lastNameFirst)
            ),
            chipsRow(
                'contacts.about.relationship-manager',
                employeeChips(getManagers(contact.managers ?? null), lastNameFirst)
            ),
            {
                label: clientAssistantLabel,
                content: '-',
                chips: employeeChips(getAdministrators(contact.administrators ?? null), lastNameFirst),
                hidden: !isAdministratorFieldEnabled,
            },
            servedBy(servedByOfficesRow, contact.servedByOffices),
            servedBy(servedByPracticeGroupsRow, contact.servedByPracticeGroups),
        ];
        // The address's Edit follows its value rather than standing at the cell's far edge; an unset address reads `-`.
        // A set one has a row of its own, as its field has in the section's form.
        const inlineEdit = (row: Row | null): Row | null =>
            row && {
                ...row,
                content: (
                    <>
                        {row.content || '-'}
                        {row.button}
                    </>
                ),
                button: undefined,
                wide: spansRow(row.content),
            };
        const relationChips = (key: 'relations' | 'relatesTo', type: string) =>
            linkChips(
                'contact',
                sortByField(contact[key] ?? [], 'otherParty.name')
                    .filter((relation) => relation.partyRelationshipTypeCode === type)
                    .map((relation) => relation.otherParty)
            );
        const personRows = filterRowList([
            ...nameRows,
            {
                label: infoLabel('contacts.display-name-unofficial', 'contacts.tooltips.unofficial-name'),
                content: contact.name,
            },
            {
                label: <FormattedMessage id="first-name-local" />,
                content: contact.localFirstName || '-',
                hidden: !isLocalizedFieldsEnabled,
            },
            {
                label: <FormattedMessage id="last-name-local" />,
                content: contact.localLastName || '-',
                hidden: !isLocalizedFieldsEnabled,
            },
            titleRow,
            positionRow,
            chipsRow('contacts.related-keywords', keywordChips),
            chipsRow('contacts.areas-of-responsibility', areaOfResponsibilityChips, {
                hintId: 'contacts.tooltips.areas-of-responsibility',
            }),
            relationshipGrowthRow,
            personalCodeRow,
            linkedinRow,
            inlineEdit(homeAddressRow),
            // The form has the local address for a client only.
            ...homeLocalAddressRows.map((row) => inlineEdit({ ...row, readOnly: !isClient })),
            chipsRow('business-areas', businessAreaChips, { hidden: !isClient }),
            chipsRow(
                'contacts.business-contact-of',
                employeeChips(getBusinessContacts(contact.businessContacts ?? null), lastNameFirst)
            ),
            chipsRow('contacts.employed-by', relationChips('relatesTo', 'EMPLOYEE')),
            segmentRow,
            serviceLevelRow,
            { ...partyNumberRow, readOnly: true },
        ]);
        const companyRows = filterRowList([
            officialNameRow,
            {
                label: infoLabel('contacts.unofficial-name', 'contacts.tooltips.unofficial-name'),
                content: contact.name,
            },
            {
                label: <FormattedMessage id="official-name-local" />,
                content: contact.officialLocalName || '-',
                hidden: !isLocalizedFieldsEnabled,
            },
            regCodeRow,
            websiteRow,
            chipsRow('business-areas', businessAreaChips),
            inlineEdit(registrationAddressRow),
            ...registrationLocalAddressRows.map(inlineEdit),
            segmentRow,
            serviceLevelRow,
            { ...partyNumberRow, readOnly: true },
        ]);
        const primaryContact = contact.relations?.find(
            (relation) => relation.partyRelationshipTypeCode === 'PRIMARY_CONTACT'
        )?.otherParty;
        const contactDetailsRows = filterRowList([
            phoneRow,
            mobileRow,
            emailRow,
            inlineEdit(correspondenceAddressRow),
            inlineEdit(visitingAddressRow),
            isPerson
                ? null
                : chipsRow(
                      'employees.employees',
                      linkChips(
                          'contact',
                          employees.map((relation) => relation.otherParty)
                      )
                  ),
            primaryContactRow && {
                ...primaryContactRow,
                // One line, as its picker shows the one contact it holds.
                content: primaryContact ? (
                    <EllipsisTooltip>
                        <PartyName
                            party={primaryContact}
                            isLink={!primaryContact.deletedAt}
                            variant="readonly-self"
                            singleLine
                            hideTitle
                        />
                    </EllipsisTooltip>
                ) : (
                    '-'
                ),
            },
            chipsRow(
                'contacts.contact-groups',
                (contact.partyGroups ?? []).map((group) => ({
                    key: group.id,
                    record: true,
                    label: <RecordLink className={LINK_CLASS} to={`/group/${group.id}`} label={group.groupName} />,
                    adornment: <GroupInfo group={group} classifiers={classifiers} />,
                }))
            ),
            primaryContactForCompaniesRow && { ...primaryContactForCompaniesRow, readOnly: true },
            uboRow && { ...uboRow, readOnly: true },
        ]);
        const marketingSectionRows: Row[] = [
            marketingRows[0],
            chipsRow(
                'contacts.marketing.mailing-preferences',
                classifierChips(
                    classifiers['MAILING_PREFERENCE_TYPE'],
                    contact.mailingPreferences?.map((item) => item.mailingPreferenceTypeCode)
                ),
                { hintId: 'contacts.marketing.tooltips.mailing-preferences' }
            ),
            chipsRow(
                'contacts.marketing.preferred-marketing-areas',
                classifierChips(
                    classifiers['MARKETING_AREA_TYPE'],
                    contact.marketingAreaTypes?.map((item) => item.marketingAreaTypeCode)
                ),
                { hintId: 'contacts.marketing.tooltips.preferred-marketing-areas' }
            ),
            chipsRow(
                'contacts.marketing.jurisdictions-of-interest',
                classifierChips(
                    classifiers['COUNTRY'],
                    contact.countries?.map((item) => item.countryTypeCode)
                ),
                { hintId: 'contacts.marketing.tooltips.jurisdictions-of-interest' }
            ),
        ];
        const employeesSectionCard = contact.partyType === 'COMPANY' && employees.length > 0 && (
            <SectionCard
                title={<FormattedMessage id="employees.employees" />}
                action={
                    onNavigateToEmployeesTab ? (
                        <Button
                            variant="link"
                            size="xs"
                            className="text-meta h-auto px-0"
                            onClick={onNavigateToEmployeesTab}
                        >
                            <FormattedMessage id="employees.all-employees" />
                        </Button>
                    ) : null
                }
            >
                <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                    {employees.slice(0, 3).map((emp) => (
                        <li key={emp.otherParty.id} className="flex min-w-0 items-start gap-2.5">
                            <Avatar className="bg-warning-tint text-warning-ink size-[30px] flex-none text-xs font-semibold">
                                {getInitials(emp.otherParty.name)}
                            </Avatar>
                            <span className="flex min-w-0 flex-col leading-[1.3]">
                                <PartyName
                                    party={emp.otherParty}
                                    isLink={!emp.otherParty.deletedAt}
                                    linkTarget="_blank"
                                    variant="readonly-other"
                                    className="font-medium"
                                />
                                {emp.otherParty.position ? (
                                    <span className="text-muted-foreground text-xs">{emp.otherParty.position}</span>
                                ) : null}
                            </span>
                        </li>
                    ))}
                </ul>
            </SectionCard>
        );
        const typeRows: Row[] = [
            {
                label: <FormattedMessage id="contacts.type" />,
                content: <FormattedMessage id={isPerson ? 'contacts.types.person' : 'contacts.types.company'} />,
                wide: 'always',
            },
        ];

        // One block per form section, each with its own Edit, on the left and the figures on the right, as on a
        // case; 2 : 1 from 1280 px, 3 : 2 from 1024 px, one column below.
        return (
            <>
                {blockingAlert}
                <div className={RECORD_GRID_CLASS}>
                    <div className="flex min-w-0 flex-col gap-(--card-gap)">
                        {isPerson
                            ? section('person', 'contacts.person.title', personRows)
                            : section('company', 'contacts.company.title', companyRows)}
                        {section('about', 'contacts.about.title', aboutRows)}
                        {section('contact-details', 'contacts.contact-details', contactDetailsRows)}
                        {isPerson && isContactDocumentFieldsEnabled
                            ? section('identity', 'contacts.identity-document.title', identityRows)
                            : null}
                        {employeesSectionCard}
                        {isPerson ? section('marketing', 'contacts.marketing.title', marketingSectionRows) : null}
                        {isContactTypeEditable ? section('choose-type', 'contacts.type-section', typeRows) : null}
                        {customFieldView ? (
                            <ContactSection id="custom-fields" titleId="custom-fields.title" contact={contact}>
                                {(edit) => (
                                    <div className="flex flex-col gap-4">
                                        {edit ? <div className="flex justify-end">{edit}</div> : null}
                                        {customFieldView}
                                    </div>
                                )}
                            </ContactSection>
                        ) : null}
                        {auditInfo}
                    </div>
                    {aside ?? figureCards}
                </div>
            </>
        );
    }

    return (
        <>
            {blockingAlert}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-12">
                {aside ? null : <div className="sm:col-span-2">{labelCardsSequence}</div>}
                <div className={cn('flex flex-col gap-6', aside ? 'sm:col-span-6' : 'sm:col-span-5')}>
                    {contact.partyType === 'COMPANY' ? (
                        <GridItemList
                            titleId="contacts.company.title"
                            size={12}
                            data={withMissing(data.company, missingFields)}
                        />
                    ) : null}
                    {contact.partyType === 'PERSON' ? (
                        <GridItemList
                            titleId="contacts.person.title"
                            size={12}
                            data={withMissing(data.person, missingFields)}
                        />
                    ) : null}
                    {customFieldView}
                    {auditInfo}
                </div>
                <div className={cn('flex flex-col gap-6', aside ? 'sm:col-span-6' : 'sm:col-span-5')}>
                    <GridItemList
                        titleId="contacts.contact-details"
                        size={12}
                        data={withMissing(filterRowList(data.contact), missingFields)}
                    />
                    {employeesCard}
                    {contact.partyType === 'PERSON' ? (
                        <GridItemList
                            titleId="contacts.marketing.title"
                            size={12}
                            data={withMissing(data.marketing, missingFields)}
                        />
                    ) : null}
                    {aside}
                </div>
            </div>
        </>
    );
};

type MarketingAreaTypesListProps = {
    marketingAreaTypes: ContactDto['marketingAreaTypes'];
    classifiers: Record<string, ClassifierData>;
};

const MarketingAreaTypesList: React.FC<MarketingAreaTypesListProps> = React.memo(
    ({ marketingAreaTypes, classifiers }) => {
        if (!marketingAreaTypes) return <div>-</div>;
        const items: React.ReactNode[] = [];
        marketingAreaTypes.forEach((item, index) => {
            const optionalComma = index < marketingAreaTypes.length - 1 ? ', ' : null;
            items.push(
                <React.Fragment key={item.marketingAreaTypeCode}>
                    {getClassifierText(classifiers['MARKETING_AREA_TYPE'], item.marketingAreaTypeCode)}
                    {optionalComma}
                </React.Fragment>
            );
        });
        return <div>{items.length ? items : '-'}</div>;
    }
);
MarketingAreaTypesList.displayName = 'MarketingAreaTypesList';

export default GeneralView;
